import {
  ApiDataProcessorType,
  ApiReturnAlert,
  ApiReturnDataProcessorConfiguration,
  ApiReturnDevice,
  ApiReturnDeviceType,
  ApiReturnMeasurePoint,
  ApiReturnMeasureType,
} from '@highimpact-frontend/api-types';
import { Box } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridPaginationModel
} from '@mui/x-data-grid-pro';
import AlertService from '../../../api/services/dataprocessors/Alert/AlertService';
import DataProcessorConfigurationService from '../../../api/services/dataprocessors/DataProcessorConfiguration/DataProcessorConfigurationService';
import DataProcessorTypeService from '../../../api/services/dataprocessors/DataProcessorType/DataProcessorTypeService';
import { useMuiDataGridLocale } from '../../../i18n/useMuiDataGridLocale';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type DataRowDetail = Record<string, any> & { endDate?: Date | string, device?: string };

export interface DataRow {
  id: string; // alert.uuid
  status: ApiReturnAlert['status'];
  date: Date;
  type: string;
  severity: ReturnType<typeof mapSeverityToLevel>;
  measurePoint: string;
  message: string; // alert message
  details: DataRowDetail;
}

function mapSeverityToLevel(severity: ApiReturnAlert['severity']) {
  switch (severity) {
    case 1:
      return 'NO_ERROR';
    case 2:
      return 'WARNING';
    case 3:
      return 'ERROR';
    case 4:
      return 'CRITICAL';
    case null:
    case undefined:
      return 'N_A';
  }
}

function buildDataRows(
  alerts: ApiReturnAlert[],
  configs: ApiReturnDataProcessorConfiguration[],
  types: ApiDataProcessorType[],
  devices: ApiReturnDevice[],
  deviceTypes: ApiReturnDeviceType[],
  measurePoints: ApiReturnMeasurePoint[],
  measureTypes: ApiReturnMeasureType[]
): DataRow[] {
  return alerts.map((a) => {
    const config = configs.find(
      (c) =>
        a.dataprocessor_configuration_id &&
        c.id === a.dataprocessor_configuration_id
    );
    const type = types.find(
      (t) =>
        config &&
        config.dataprocessor_type_id &&
        t.id === config.dataprocessor_type_id
    );
    const device = devices.find((d) => d.id === a.device_id);
    const deviceType = deviceTypes.find(
      (dt) => device && device.device_type_id && dt.id === device.device_type_id
    );
    const measurePoint = measurePoints.find(
      (mp) => a.measure_point_id && mp.id === a.measure_point_id
    );
    const measureTypeId = a.data?.measure_type_id;
    const measureType = measureTypeId
      ? measureTypes.find((mt) => mt.id === measureTypeId)
      : undefined;
    if (measureTypeId && a.data) {
      delete a.data.measure_type_id;
    }
    return {
      id: a.uuid,
      status: a.status,
      date: new Date(a.start_date),
      type: type?.name ?? 'N/A',
      severity: mapSeverityToLevel(a.severity),
      message: a.message ?? 'N/A',
      measurePoint: measurePoint?.name ?? 'N/A',
      details: {
        endDate: (a.end_date && new Date(a.end_date).toLocaleString()) || 'N/A',
        device: device?.name ?? 'N/A',
        measureType: measureType?.name ?? 'N/A',
        ...a.data,
      },
    };
  });
}

interface AlertTableProps {
  projectId: number;
  siteId: number;
  devices: ApiReturnDevice[];
  deviceTypes: ApiReturnDeviceType[];
  measurePoints: ApiReturnMeasurePoint[];
  measureTypes: ApiReturnMeasureType[];
}

export const AlertList: React.FC<AlertTableProps> = ({
  projectId,
  siteId,
  devices,
  deviceTypes,
  measurePoints,
  measureTypes,
}) => {
  const [alerts, setAlerts] = useState<ApiReturnAlert[]>([]);
  const [configs, setConfigs] = useState<ApiReturnDataProcessorConfiguration[]>(
    []
  );
  const [types, setTypes] = useState<ApiDataProcessorType[]>([]);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState<number>(0);
  const toPascalCase = (str: string): string =>
    (str.match(/[a-zA-Z0-9]+/g) || []).map(w => w[0].toUpperCase() + w.slice(1).toLowerCase()).join('');
  const { t } = useTranslation('alerts');
  const localeText = useMuiDataGridLocale();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const limit = paginationModel.pageSize;
      const offset = paginationModel.page * paginationModel.pageSize;

      try {
        const [alertsData, configsData, typesData] = await Promise.all([
          AlertService.getAll(projectId, siteId, limit, offset),
          DataProcessorConfigurationService.getAll(projectId, siteId),
          DataProcessorTypeService.getAll(projectId),
        ]);
        setRowCount(alertsData.total);
        setAlerts(alertsData.alerts);
        setConfigs(configsData);
        setTypes(typesData);
      } catch (err) {
        console.error('Error loading alerts/configurations/types', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId, siteId, paginationModel]);

  const rows: DataRow[] = useMemo(
    () =>
      buildDataRows(
        alerts,
        configs,
        types,
        devices,
        deviceTypes,
        measurePoints,
        measureTypes
      ),
    [alerts, configs, types, devices, deviceTypes, measurePoints]
  );

  const columns: GridColDef<DataRow>[] = [
    {
      field: 'date',
      headerName: t('Date'),
      type: "dateTime",
      maxWidth: 200,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
      renderCell: (params) => params.row.date.toLocaleString(),
    },
    {
      field: 'status',
      headerName: t('Statut'),
      type: 'singleSelect',
      valueOptions: ['active', 'preactive', 'inactive', 'over'],
      flex: 1,
      maxWidth: 150,
      renderCell: (params: GridRenderCellParams) => {
        const color =
          params.row.status === 'active'
            ? 'green'
            : params.value === 'preactive'
              ? 'orange'
              : params.value === 'inactive'
                ? 'gray'
                : 'red';
        return (
          <span style={{ color, fontWeight: 600 }}>
            {toPascalCase(params.value)}
          </span>
        );
      },
    },
    {
      field: 'severity',
      headerName: t('Niveau'),
      maxWidth: 150,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
      renderCell: (params): JSX.Element => {
        switch (params.row.severity) {
          case 'NO_ERROR':
            return <span style={{ color: 'green' }}>{t("Pas d'erreur")}</span>;
          case 'WARNING':
            return (
              <span style={{ color: 'orange' }}>{t('Avertissement')}</span>
            );
          case 'ERROR':
            return <span style={{ color: 'red' }}>{t('Erreur')}</span>;
          case 'CRITICAL':
            return (
              <span style={{ color: 'darkred', fontWeight: 'bold' }}>
                {t('Critique')}
              </span>
            );
          case 'N_A':
            return <span>N/A</span>;
          // We don't include a `default` in order to get a type error if we forget a case
        }
      },
    },
    {
      field: 'measurePoint',
      headerName: t('Station de mesure'),
      maxWidth: 200,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },
    {
      field: 'type',
      headerName: t("Type d'alerte"),
      maxWidth: 300,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
      renderCell: (params) => (params.value ? t(`${params.value}`) : t('N/A')),
    },
    {
      field: 'message',
      headerName: t('Message'),
      minWidth: 200,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    }
  ];

  return (
    <Box
      sx={{
        height: 'calc(100vh - 200px - 20px)',
        overflow: 'auto',
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <DataGridPro
        loading={loading}
        localeText={localeText}
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        getRowId={(row) => row.id}
        pageSizeOptions={[5, 10, 25, 50]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pagination
        paginationMode='server'
        initialState={{
          filter: {
            filterModel: {
              items: [
                { field: 'status', operator: 'isAnyOf', value: ['active', 'over'] }
              ]
            }
          }
        }}
        // initialSortModel={[
        //   { field: 'type', sort: 'asc' },
        //   { field: 'model', sort: 'asc' },
        //   { field: 'serialNumber', sort: 'asc' },
        // ]}
        slotProps={{
          columnMenu: {
            slots: {
              columnMenuSortItem: null,
              columnMenuColumnsItem: null,
              columnMenuPinningItem: null,
            },
          },
        }}
        getDetailPanelContent={({ row }) => {
          const formatKeyLabel = (key: string): string =>
            key
              .replace(/_/g, ' ')
              .replace(/^./, (char) => char.toUpperCase());
          return (
            <Box
              sx={{
                pl: '2.5%',
                display: 'flex',
                alignItems: 'center',
                width: '30%',
                overflow: 'hidden',
              }}>
              <DataGridPro
                getRowHeight={() => 35}
                rows={Object.entries(row.details).map(([key, value], index) => ({
                  id: index,
                  key: t(formatKeyLabel(key)),
                  value: t(String(value)),
                }))}
                columns={[
                  { field: 'key', headerName: '', flex: 1 },
                  { field: 'value', headerName: '', flex: 1 },
                ]}
                hideFooter
                slots={{
                  columnHeaders: () => null,
                }}
                disableColumnMenu
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-virtualScroller': {
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  },
                  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                    display: 'none',
                  },
                  '& .MuiDataGrid-scrollbar': {
                    display: 'none',
                  },
                }}
              />
            </Box>
          )
        }
        }
        getDetailPanelHeight={() => 'auto'}
      />
    </Box >
  );
};

export default AlertList;
